import type { WeldTelemetrySampleRow } from './weldTelemetryClient'
import { convertWeldDataValue } from './weldUnitUtils'

export type SortDirection = 'asc' | 'desc' | null

export interface SortState {
  columnKey: string | null
  direction: SortDirection
}

export const INITIAL_SORT_STATE: SortState = { columnKey: null, direction: null }

/**
 * Cycle the sort state by one click on `columnKey`.
 *
 * Behaviour:
 *   - clicking the same column: `asc` → `desc` → `none` → `asc` …
 *   - clicking a different column: always resets that column to `asc`.
 *
 * Returning `{ columnKey: null, direction: null }` is the canonical "no sort"
 * state and signals the caller to restore the original order.
 */
export function cycleSort(prev: SortState, columnKey: string): SortState {
  if (prev.columnKey !== columnKey) {
    return { columnKey, direction: 'asc' }
  }
  if (prev.direction === 'asc') return { columnKey, direction: 'desc' }
  if (prev.direction === 'desc') return { columnKey: null, direction: null }
  return { columnKey, direction: 'asc' }
}

/**
 * Mapping from a sortable column key to the parameter key used for unit
 * conversion. Keys absent from this map are either unit-invariant or
 * non-numeric and handled directly by `getSortValue`.
 */
const COLUMN_PARAM_KEYS: Record<string, string> = {
  tilt: 'tilt',
  distance: 'distance',
  travelSpeed: 'travelSpeed',
  volts: 'volts',
  amps: 'amps',
  wireIpm: 'wireIpm',
  widthMil: 'widthMil',
  targetVlt: 'targetVlt',
  csBias: 'csBias',
  bpm: 'bpm',
  totWireIn: 'totWireIn',
  trueEnergy: 'trueEnergy',
  heat: 'rprtHeat'
}

/**
 * Return the comparable value for one column / row pair. Numeric columns are
 * converted through the current unit setting so the sort order matches what
 * the user sees in the cell. Returns `null` for missing data (sorted last).
 */
export function getSortValue(
  sample: WeldTelemetrySampleRow,
  columnKey: string,
  originalIndex: number
): number | string | null {
  switch (columnKey) {
    case 'sl-no':
      return originalIndex
    case 'event':
      return sample.evtNo
    case 'time':
      return Number.isFinite(sample.sampleTime) ? sample.sampleTime : null
    case 'torch':
      return sample.torch
    case 'bugType':
      return sample.bugType ?? '—'
    case 'duration':
      return Number.isFinite(sample.duration ?? NaN) ? (sample.duration as number) : null
    case 'heat': {
      const raw = sample.rprtHeat ?? sample.calcHeat
      return convertWeldDataValue(raw ?? null, 'rprtHeat')
    }
    default: {
      const paramKey = COLUMN_PARAM_KEYS[columnKey]
      if (!paramKey) return null
      const raw = (sample as unknown as Record<string, unknown>)[columnKey]
      const num = typeof raw === 'number' ? raw : null
      return convertWeldDataValue(num, paramKey)
    }
  }
}

function isMissing(value: number | string | null): boolean {
  if (value === null) return true
  if (typeof value === 'number') return !Number.isFinite(value)
  return false
}

/**
 * Sort `samples` according to `state`. `null` / `NaN` values are always pushed
 * to the end regardless of direction; ties keep the original insertion order
 * (stable). Returns the original array reference when `direction === null`
 * so callers can identity-check to skip downstream work.
 */
export function sortSamples(
  samples: WeldTelemetrySampleRow[],
  state: SortState
): WeldTelemetrySampleRow[] {
  if (state.direction === null || state.columnKey === null) {
    return samples
  }

  const columnKey = state.columnKey
  const indexed = samples.map((sample, index) => ({
    sample,
    index,
    sortValue: getSortValue(sample, columnKey, index)
  }))

  const direction = state.direction === 'asc' ? 1 : -1

  indexed.sort((a, b) => {
    const aMissing = isMissing(a.sortValue)
    const bMissing = isMissing(b.sortValue)
    if (aMissing && bMissing) return a.index - b.index
    if (aMissing) return 1
    if (bMissing) return -1

    if (typeof a.sortValue === 'string' && typeof b.sortValue === 'string') {
      const cmp = a.sortValue.localeCompare(b.sortValue)
      if (cmp !== 0) return direction * cmp
      return a.index - b.index
    }
    const an = a.sortValue as number
    const bn = b.sortValue as number
    if (an !== bn) return direction * (an - bn)
    return a.index - b.index
  })

  return indexed.map((entry) => entry.sample)
}
